import {
  Icon,
  IconButton,
  type IconButtonProps,
  type IconProps,
  VisuallyHidden,
} from '@chakra-ui/react'
import { type ConditionalValue } from '@chakra-ui/react'
import React, { forwardRef, useEffect, useMemo, useRef } from 'react'
import { Tooltip } from '~/components/ui/tooltip'
import { Size } from '~/types/ThemeTypes'
import { getNodeText } from '~/utils/objectUtils'

import {
  getQuickActionButtonStyles as mergeQuickActionButtonStyles,
  QuickActionButtonVariants,
} from './componentStyles/QuickActionButton.style'

const shallowEqual = <T extends object>(
  a: T | undefined,
  b: T | undefined,
): boolean => {
  if (a === b) return true
  if (!a || !b) return false
  const keysA = Object.keys(a) as (keyof T)[]
  const keysB = Object.keys(b)
  if (keysA.length !== keysB.length) return false
  return keysA.every((key) => Object.is(a[key], b[key]))
}

const FOCUS_VISIBLE_SELECTOR = ':focus-visible'

export type QuickActionButtonSizes =
  Size.xs | Size.sm | Size.md | Size.lg | Size.xl

interface QuickActionButtonProps extends Omit<
  IconButtonProps,
  'aria-label' | 'variant'
> {
  variant: QuickActionButtonVariants
  size?: ConditionalValue<QuickActionButtonSizes>
  actionIcon: React.ComponentType<any>
  tooltipText: string | React.ReactNode
  /** Accessible name; defaults to the text content of `tooltipText`. */
  ariaLabel?: string
  /** Accessible name rendered as visually hidden text (masked in session replay); use instead of ariaLabel when it contains names or numbers. */
  accessibleLabel?: string
  tooltipDisabled?: boolean
  iconProps?: IconProps
  isActive?: boolean
}
export const QuickActionButton = forwardRef(function QuickActionButton(
  {
    variant,
    size,
    actionIcon,
    tooltipText,
    ariaLabel,
    accessibleLabel,
    tooltipDisabled,
    iconProps,
    disabled,
    isActive,
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
    ...rest
  }: QuickActionButtonProps,
  ref: React.Ref<HTMLButtonElement> | undefined,
) {
  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false)
  const focusOpenedTriggerRef = useRef<HTMLButtonElement | null>(null)
  const hasTooltip =
    tooltipDisabled !== true && !disabled && Boolean(tooltipText)
  const isTooltipShown = isTooltipOpen && hasTooltip

  // Window capture runs before every document-level Escape listener (dialogs,
  // panels), whatever order they registered in, so they see the key as handled.
  useEffect(() => {
    if (!isTooltipShown) return
    const claimEscape = (event: KeyboardEvent) => {
      if (
        event.key === 'Escape' &&
        focusOpenedTriggerRef.current != null &&
        event.target === focusOpenedTriggerRef.current
      ) {
        event.preventDefault()
      }
    }
    window.addEventListener('keydown', claimEscape, true)
    return () => window.removeEventListener('keydown', claimEscape, true)
  }, [isTooltipShown])

  const tooltipAriaLabel = useMemo(() => {
    if (accessibleLabel) return undefined
    return ariaLabel || getNodeText(tooltipText)
  }, [accessibleLabel, ariaLabel, tooltipText])

  const stylesMemoKey = `${variant}-${size}`
  const styles = useMemo(
    () => mergeQuickActionButtonStyles({ variant, size }),
    [stylesMemoKey],
  )

  const iconButtonProps = useMemo(
    () => ({
      display: styles.iconButton?.display,
      alignItems: styles.iconButton?.alignItems,
      justifyContent: styles.iconButton?.justifyContent,
    }),
    [styles],
  )

  const iconPropsRef = useRef(iconProps)
  if (!shallowEqual(iconPropsRef.current, iconProps)) {
    iconPropsRef.current = iconProps
  }

  const iconStyles = useMemo(
    () => ({ ...styles.icon, ...iconPropsRef.current }),
    [styles, iconPropsRef.current],
  )

  const ActionIconComp = actionIcon

  return (
    <Tooltip
      open={isTooltipShown}
      onOpenChange={(details) => {
        if (!details.open) setIsTooltipOpen(false)
      }}
      showArrow
      positioning={{ placement: 'top' }}
      content={tooltipText}
      disabled={disabled}
    >
      <IconButton
        size={size}
        data-group
        data-active={isActive ? 'true' : undefined}
        ref={ref}
        css={styles.iconButton}
        {...iconButtonProps}
        aria-label={tooltipAriaLabel}
        disabled={disabled}
        {...rest}
        className={`group ${rest.className || ''}`}
        onMouseEnter={(e) => {
          setIsTooltipOpen(true)
          if (onMouseEnter) onMouseEnter(e)
        }}
        onMouseLeave={(e) => {
          setIsTooltipOpen(false)
          if (onMouseLeave) onMouseLeave(e)
        }}
        onFocus={(e) => {
          if (e.currentTarget.matches(FOCUS_VISIBLE_SELECTOR)) {
            focusOpenedTriggerRef.current = e.currentTarget
            setIsTooltipOpen(true)
          }
          if (onFocus) onFocus(e)
        }}
        onBlur={(e) => {
          focusOpenedTriggerRef.current = null
          setIsTooltipOpen(false)
          if (onBlur) onBlur(e)
        }}
      >
        <Icon css={iconStyles}>
          <ActionIconComp size="100%" />
        </Icon>
        {accessibleLabel && <VisuallyHidden>{accessibleLabel}</VisuallyHidden>}
      </IconButton>
    </Tooltip>
  )
})
